import type {
  BreadcrumbPartSibling,
  BreadcrumbPartType,
} from '@ipen-portal/plugin-navigation-react';
import type { FC, MouseEvent, ReactElement } from 'react';

import { Link } from '@backstage/core-components';
import { Icon } from '@iconify/react';
import { IconButton, Menu, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { tss } from 'tss-react/mui';

import { SiblingMenuItem } from './components/SiblingMenuItem';

const useStyles = tss.create(({ theme }) => ({
  activeName: {
    fontWeight: 600,
  },
  expand: {
    marginLeft: theme.spacing(1),
  },
  icon: {
    color: 'inherit',
    marginLeft: theme.spacing(1),
    padding: 0,
  },
  name: {
    color: 'inherit',
    fontWeight: 100,
    textTransform: 'capitalize',
    [theme.breakpoints.down('sm')]: {
      maxWidth: 200,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    margin: 0,
  },
}));

type Group = GroupDefinition & {
  items: BreadcrumbPartSibling[];
};
type GroupDefinition = {
  icon?: ReactElement | string;
  title: string;
};

type Props = BreadcrumbPartType & {
  current: boolean;
  groupDefinitions?: Record<string, GroupDefinition>;
  titleOrder?: Record<string, number>;
};

const sortWithTitleOrder = (
  result: { title: string }[],
  titleOrder: Record<string, number> | undefined,
) => {
  if (!titleOrder) return;
  result.sort(
    (a, b) =>
      (titleOrder[a.title.toLowerCase()] ?? 9999)
      - (titleOrder[b.title.toLowerCase()] ?? 9999),
  );
};

export const BreadcrumbPart: FC<Props> = ({
  current,
  fullTitle,
  groupDefinitions,
  link,
  siblings,
  titleOrder,
}) => {
  const { classes, cx } = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openedGroup, setOpenedGroup] = useState<null | {
    anchor: HTMLElement;
    group: Group;
  }>(null);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const groupedSiblings = useMemo(() => {
    const result: (BreadcrumbPartSibling | Group)[] = [];
    const groups = new Map<string, Group>();
    siblings?.forEach(sibling => {
      if (groupDefinitions && sibling.group) {
        let group = groups.get(sibling.group);
        if (!group) {
          const groupDefinition = groupDefinitions[sibling.group];
          if (groupDefinition) {
            group = {
              icon: groupDefinition.icon,
              items: [],
              title: groupDefinition.title,
            };
            groups.set(sibling.group, group);
            result.push(group);
          }
        }
        if (group) {
          group.items.push(sibling);
          return;
        }
      }
      result.push(sibling);
    });

    sortWithTitleOrder(result, titleOrder);
    for (const item of result) {
      if ('items' in item) {
        sortWithTitleOrder(item.items, titleOrder);
      }
    }
    return result;
  }, [siblings, titleOrder, groupDefinitions]);

  const nameComponent = (
    <Typography
      className={cx(classes.name, {
        [classes.activeName]: current,
      })}
      key={fullTitle}
    >
      {fullTitle}
    </Typography>
  );
  return (
    <li aria-label={fullTitle} className={classes.root}>
      {link
        ? (
            <Link key={fullTitle} to={link}>
              {nameComponent}
            </Link>
          )
        : (
            nameComponent
          )}
      {!!siblings?.length && (
        <>
          <IconButton
            className={classes.icon}
            edge="end"
            onClick={handleClick}
            size="small"
            title="Menu Button"
          >
            <Icon height={24} icon="material-symbols:expand-more" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              horizontal: 'right',
              vertical: 'bottom',
            }}
            MenuListProps={{
              'aria-label': 'Breadcrumb Menu',
            }}
            onClose={handleClose}
            open={Boolean(anchorEl)}
            transformOrigin={{
              horizontal: 'center',
              vertical: 'top',
            }}
          >
            {groupedSiblings.map(itemOrGroup => {
              const isGroup = 'items' in itemOrGroup;
              return (
                <SiblingMenuItem
                  key={itemOrGroup.title}
                  onClick={
                    isGroup
                      ? e =>
                        setOpenedGroup({
                          anchor: e.currentTarget,
                          group: itemOrGroup,
                        })
                      : handleClose
                  }
                  sibling={itemOrGroup}
                >
                  {isGroup && (
                    <Icon
                      className={classes.expand}
                      height={16}
                      icon="material-symbols:chevron-right"
                    />
                  )}
                </SiblingMenuItem>
              );
            })}
          </Menu>
          {openedGroup && (
            <Menu
              anchorEl={openedGroup.anchor}
              anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
              onClose={() => setOpenedGroup(null)}
              open
              transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            >
              {openedGroup.group.items.map(sibling => (
                <SiblingMenuItem
                  key={sibling.fullTitle}
                  onClick={() => {
                    setOpenedGroup(null);
                    handleClose();
                  }}
                  sibling={sibling}
                />
              ))}
            </Menu>
          )}
        </>
      )}
    </li>
  );
};
